// SPDX-License-Identifier: AGPL-3.0-or-later

// Chrome and Edge fire `beforeinstallprompt` once, early, before any React
// component exists. Catch it at startup so the install card can open the
// browser's own install window later.

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function notify(): void {
	for (const listener of listeners) listener();
}

export function captureInstallPrompt(): void {
	if (typeof window === 'undefined') return;
	window.addEventListener('beforeinstallprompt', (event) => {
		event.preventDefault();
		deferred = event as BeforeInstallPromptEvent;
		notify();
	});
	window.addEventListener('appinstalled', () => {
		deferred = null;
		installed = true;
		notify();
	});
}

export function canPromptInstall(): boolean {
	return deferred !== null;
}

export function wasJustInstalled(): boolean {
	return installed;
}

export async function promptInstall(): Promise<boolean> {
	const event = deferred;
	if (!event) return false;
	deferred = null;
	await event.prompt();
	const {outcome} = await event.userChoice;
	notify();
	return outcome === 'accepted';
}

export function subscribeInstallPrompt(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}
